import { useRef, useState } from 'react';
import { createModule, useLightboxState, type Module } from 'yet-another-react-lightbox';
import styles from './Gallery.module.scss';

const galleryDotpaginationModule = createModule('GalleryDotPagination', () => {
  const { slides, currentIndex } = useLightboxState();
  const windowStartRef = useRef(0);
  const prevIndexRef = useRef(currentIndex);
  const [animatingDots, setAnimatingDots] = useState<{
    expandingPosition: number;
    contractingPosition: number;
  } | null>(null);

  if (!slides || slides.length <= 1) return null;

  const totalSlides = slides.length;
  const maxDots = 5;

  // Calculate which dots to show
  const getDotIndices = () => {
    if (totalSlides <= maxDots) {
      return Array.from({ length: totalSlides }, (_, i) => i);
    }

    let windowStart = windowStartRef.current;
    const prevWindowStart = windowStart;

    // Check if current index is outside the current window
    if (currentIndex < windowStart) {
      windowStart = currentIndex;
    } else if (currentIndex >= windowStart + maxDots) {
      windowStart = currentIndex - maxDots + 1;
    }

    // Ensure window doesn't go beyond bounds
    windowStart = Math.max(0, Math.min(windowStart, totalSlides - maxDots));

    // Check if we're shifting and need animation
    if (windowStart !== prevWindowStart) {
      const prevIndex = prevIndexRef.current;

      // If moving right and shifting window right
      if (currentIndex > prevIndex && windowStart > prevWindowStart) {
        setAnimatingDots({
          expandingPosition: 4, // rightmost dot expands
          contractingPosition: 3, // previous active dot contracts
        });
        setTimeout(() => setAnimatingDots(null), 600);
      }
      // If moving left and shifting window left
      else if (currentIndex < prevIndex && windowStart < prevWindowStart) {
        setAnimatingDots({
          expandingPosition: 0, // leftmost dot expands
          contractingPosition: 1, // previous active dot contracts
        });
        setTimeout(() => setAnimatingDots(null), 600);
      }
    }

    // Update refs for next render
    windowStartRef.current = windowStart;
    prevIndexRef.current = currentIndex;

    return Array.from({ length: maxDots }, (_, i) => windowStart + i);
  };

  const dotIndices = getDotIndices();

  return (
    <div className={styles['gallery-dot-pagination']}>
      {dotIndices.map((slideIndex, position) => {
        let isActive = slideIndex === currentIndex;
        let animationClass = '';

        // Handle animation states
        if (animatingDots) {
          if (position === animatingDots.expandingPosition) {
            animationClass = styles['gallery-dot--expanding'];
            isActive = false; // Remove normal active state during animation
          } else if (position === animatingDots.contractingPosition) {
            animationClass = styles['gallery-dot--contracting'];
            isActive = false; // Remove normal active state during animation
          }
        }

        return (
          <div
            key={slideIndex}
            className={`${styles['gallery-dot']} ${isActive ? styles['gallery-dot--active'] : ''} ${animationClass}`}
          />
        );
      })}
    </div>
  );
});

const DotpaginationPlugin = ({ addChild }: { addChild: (target: string, module: Module) => void }) =>
  addChild('controller', galleryDotpaginationModule);

export default DotpaginationPlugin;
